import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import StrikingForceService from "../../services/strikingforce.service";

// Fetch all striking forces
export const fetchStrikingForces = createAsyncThunk(
    "strikingForce/fetchStrikingForces",
    async (params = {}, { rejectWithValue }) => {
        try {
            return await StrikingForceService.getStrikingForces(params);
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message ||
                error.message ||
                "Failed to fetch striking forces"
            );
        }
    }
);

// Fetch striking force by ID
export const fetchStrikingForceById = createAsyncThunk(
    "strikingForce/fetchStrikingForceById",
    async (id, { rejectWithValue }) => {
        try {
            return await StrikingForceService.getStrikingForceById(id);
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message ||
                error.message ||
                "Failed to fetch striking force"
            );
        }
    }
);

// Create striking force
export const createStrikingForce = createAsyncThunk(
    "strikingForce/createStrikingForce",
    async (payload, { rejectWithValue }) => {
        try {
            return await StrikingForceService.addStrikingForce(payload);
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message ||
                error.message ||
                "Failed to create striking force"
            );
        }
    }
);

// Update striking force
export const editStrikingForce = createAsyncThunk(
    "strikingForce/editStrikingForce",
    async ({ id, payload }, { rejectWithValue }) => {
        try {
            return await StrikingForceService.updateStrikingForce(id, payload);
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message ||
                error.message ||
                "Failed to update striking force"
            );
        }
    }
);

const initialState = {
    strikingForces: [],
    selectedStrikingForce: null,

    loading: false,
    submitting: false,

    error: null,
    success: false,
    message: null,
};

const strikingForceSlice = createSlice({
    name: "strikingForce",
    initialState,

    reducers: {
        clearStrikingForceError: (state) => {
            state.error = null;
        },

        clearStrikingForceSuccess: (state) => {
            state.success = false;
            state.message = null;
        },

        clearSelectedStrikingForce: (state) => {
            state.selectedStrikingForce = null;
        },
    },

    extraReducers: (builder) => {
        builder

            // Fetch all striking forces
            .addCase(fetchStrikingForces.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchStrikingForces.fulfilled, (state, action) => {
                state.loading = false;

                const response = action.payload;

                state.strikingForces =
                    response?.data ||
                    response?.strikingForces ||
                    [];
            })
            .addCase(fetchStrikingForces.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // Fetch striking force by ID
            .addCase(fetchStrikingForceById.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchStrikingForceById.fulfilled, (state, action) => {
                state.loading = false;

                const response = action.payload;

                state.selectedStrikingForce =
                    response?.data ||
                    response?.strikingForce ||
                    response;
            })
            .addCase(fetchStrikingForceById.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // Create striking force
            .addCase(createStrikingForce.pending, (state) => {
                state.submitting = true;
                state.error = null;
                state.success = false;
            })
            .addCase(createStrikingForce.fulfilled, (state, action) => {
                state.submitting = false;
                state.success = true;

                const response = action.payload;

                const newStrikingForce =
                    response?.data ||
                    response?.strikingForce;

                if (newStrikingForce) {
                    state.strikingForces.unshift(newStrikingForce);
                }

                state.message =
                    response?.message || "Striking force created successfully";
            })
            .addCase(createStrikingForce.rejected, (state, action) => {
                state.submitting = false;
                state.error = action.payload;
            })

            // Update striking force
            .addCase(editStrikingForce.pending, (state) => {
                state.submitting = true;
                state.error = null;
                state.success = false;
            })
            .addCase(editStrikingForce.fulfilled, (state, action) => {
                state.submitting = false;
                state.success = true;

                const response = action.payload;

                const updatedStrikingForce =
                    response?.data ||
                    response?.strikingForce;

                if (updatedStrikingForce?._id) {
                    const index = state.strikingForces.findIndex(
                        (item) => item._id === updatedStrikingForce._id
                    );

                    if (index !== -1) {
                        state.strikingForces[index] = updatedStrikingForce;
                    }

                    if (
                        state.selectedStrikingForce?._id ===
                        updatedStrikingForce._id
                    ) {
                        state.selectedStrikingForce = updatedStrikingForce;
                    }
                }

                state.message =
                    response?.message || "Striking force updated successfully";
            })
            .addCase(editStrikingForce.rejected, (state, action) => {
                state.submitting = false;
                state.error = action.payload;
            });
    },
});

export const {
    clearStrikingForceError,
    clearStrikingForceSuccess,
    clearSelectedStrikingForce,
} = strikingForceSlice.actions;

export default strikingForceSlice.reducer;